import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowsClockwise,
  CheckCircle,
  MapPin,
  Warning,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import api from '../services/api';
import AddressValidationModal from './AddressValidationModal';

/**
 * USPS address-validation widget. Two render modes:
 * - `showInline=true`: renders a "Verify Address" button (used inside other forms)
 * - default: renders a labeled section (used as a stand-alone form module)
 *
 * On validation success, opens `AddressValidationModal` with the standardized
 * address + corrections; the user can accept or keep their original entry.
 */
const validatePayload = (address) => ({
  street_address: address.street_address,
  secondary_address: address.secondary_address || address.address2,
  city: address.city,
  state: address.state,
  zip_code: address.zip_code || address.zip,
});

const handleValidationOutcome = ({ result, onValidated, setShowModal }) => {
  const { is_valid, corrections, needs_confirmation, standardized_address } = result;
  // Show the comparison modal whenever the user needs to make a decision —
  // either USPS suggested corrections, asked for confirmation, or rejected
  // the address outright. Only a clean pass auto-confirms.
  if (!is_valid || corrections?.length > 0 || needs_confirmation) {
    setShowModal(true);
    return;
  }
  toast.success('Address verified');
  onValidated?.(standardized_address);
};

const ValidationResultBanner = ({ validationResult }) => {
  if (!validationResult) return null;
  const ok = validationResult.is_valid;
  return (
    <div
      className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
        ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
      }`}
    >
      {ok ? <CheckCircle size={16} /> : <Warning size={16} />}
      <span>
        {ok ? 'Address verified' : (validationResult.error || 'Address could not be verified')}
      </span>
    </div>
  );
};

const AddressValidation = ({
  address,
  onAddressChange,
  onValidated,
  showInline = false,
  className = '',
}) => {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleValidate = async () => {
    if (!address.street_address) {
      toast.error('Please enter a street address');
      return;
    }
    setValidating(true);
    try {
      const response = await api.post('/address/validate', validatePayload(address));
      setValidationResult(response.data);
      handleValidationOutcome({
        result: response.data,
        onValidated,
        setShowModal,
      });
    } catch {
      toast.error('Address validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleAcceptStandardized = () => {
    const std = validationResult?.standardized_address;
    if (std) {
      onAddressChange({
        ...address,
        street_address: std.street_address,
        secondary_address: std.secondary_address,
        city: std.city,
        state: std.state,
        zip_code: std.full_zip || std.zip_code,
      });
      onValidated?.(std);
      toast.success('Address updated');
    }
    setShowModal(false);
  };

  const handleKeepOriginal = () => {
    toast.info('Keeping original address');
    setShowModal(false);
  };

  const verifyButton = (label) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleValidate}
      disabled={validating || !address.street_address}
      data-testid="validate-address-btn"
    >
      {validating ? (
        <ArrowsClockwise size={14} className="mr-1 animate-spin" />
      ) : (
        <MapPin size={14} className="mr-1" />
      )}
      {label}
    </Button>
  );

  const modal = (
    <AddressValidationModal
      open={showModal}
      onOpenChange={setShowModal}
      result={validationResult}
      onAcceptStandardized={handleAcceptStandardized}
      onKeepOriginal={handleKeepOriginal}
    />
  );

  if (showInline) {
    return (
      <div className={className}>
        {verifyButton('Verify Address')}
        {modal}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Address Validation</Label>
        {verifyButton('Verify with USPS')}
      </div>
      <ValidationResultBanner validationResult={validationResult} />
      {modal}
    </div>
  );
};

export default AddressValidation;
